import {
  DashboardSquare01Icon,
  Calendar03Icon,
  Clock01Icon,
  UserGroupIcon,
  UserMultipleIcon,
  UserCheck01Icon,
  BookOpen01Icon,
  Building03Icon,
  DollarSquareIcon,
  InvoiceIcon,
  PackageIcon,
  WhatsappIcon,
  Mail01Icon,
  File01Icon,
  Settings02Icon,
} from '@hugeicons/core-free-icons'

export type Role = 'admin' | 'staff' | 'teacher'

interface NavItem {
  title: string
  href: string
  icon: typeof DashboardSquare01Icon
  roles: Role[]
}

interface NavGroup {
  label: string
  items: NavItem[]
  collapsible?: boolean
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Vista general',
    items: [
      { title: 'Inicio', href: '/dashboard', icon: DashboardSquare01Icon, roles: ['admin', 'staff'] },
      { title: 'Calendario', href: '/calendar', icon: Calendar03Icon, roles: ['admin', 'staff'] },
    ],
  },
  {
    // Portal del profesor: solo ve sus cursos y sus estudiantes.
    label: 'Mi aula',
    items: [
      { title: 'Mis horas', href: '/aula/horas', icon: Clock01Icon, roles: ['teacher'] },
      { title: 'Mis cursos', href: '/aula/cursos', icon: BookOpen01Icon, roles: ['teacher'] },
      { title: 'Mis estudiantes', href: '/aula/estudiantes', icon: UserGroupIcon, roles: ['teacher'] },
    ],
  },
  {
    label: 'Academia',
    items: [
      { title: 'Contactos', href: '/contacts', icon: UserMultipleIcon, roles: ['admin', 'staff'] },
      { title: 'Estudiantes', href: '/students', icon: UserGroupIcon, roles: ['admin', 'staff'] },
      { title: 'Profesores', href: '/teachers', icon: UserCheck01Icon, roles: ['admin', 'staff'] },
      { title: 'Cursos', href: '/courses', icon: BookOpen01Icon, roles: ['admin', 'staff'] },
    ],
  },
  {
    label: 'Finanzas',
    items: [
      { title: 'Horas de profesores', href: '/teacher-hours', icon: Clock01Icon, roles: ['admin'] },
      { title: 'Ingresos y gastos', href: '/financial', icon: DollarSquareIcon, roles: ['admin'] },
      { title: 'Productos', href: '/products', icon: PackageIcon, roles: ['admin'] },
      { title: 'Facturación', href: '/facturacion', icon: InvoiceIcon, roles: ['admin'] },
    ],
  },
  {
    label: 'Comunicación',
    collapsible: true,
    items: [
      { title: 'WhatsApp enviados', href: '/whatsapp', icon: WhatsappIcon, roles: ['admin', 'staff'] },
      { title: 'Correos enviados', href: '/emails', icon: Mail01Icon, roles: ['admin', 'staff'] },
      { title: 'Plantillas de WhatsApp', href: '/whatsapp-templates', icon: File01Icon, roles: ['admin', 'staff'] },
      { title: 'Plantillas de correo', href: '/email-templates', icon: File01Icon, roles: ['admin', 'staff'] },
    ],
  },
  {
    label: 'Configuración',
    collapsible: true,
    items: [
      { title: 'Datos de la sucursal', href: '/settings/branch', icon: Settings02Icon, roles: ['admin'] },
      { title: 'Facturación electrónica', href: '/settings/facturacion', icon: InvoiceIcon, roles: ['admin'] },
      { title: 'Usuarios', href: '/settings/users', icon: UserGroupIcon, roles: ['admin'] },
      { title: 'Sucursales', href: '/settings/branches', icon: Building03Icon, roles: ['admin'] },
      { title: 'Conexiones de WhatsApp', href: '/whatsapp-sessions', icon: WhatsappIcon, roles: ['admin'] },
    ],
  },
]

export function navigationContext(pathname: string) {
  const item = NAV_GROUPS.flatMap((group) => group.items).find(
    (entry) => pathname === entry.href || pathname.startsWith(`${entry.href}/`),
  )
  if (!item) return { title: pathname === '/docs' ? 'Guía de uso' : pathname === '/profile' ? 'Mi perfil' : 'St. George', href: null }
  const suffix = pathname.slice(item.href.length)
  const detail = suffix.endsWith('/inscripcion') ? 'Inscripción y pago' : suffix === '/new' ? 'Nuevo registro' : suffix.endsWith('/edit') ? 'Editar' : suffix ? 'Detalle' : null
  return { title: item.title, href: item.href, detail }
}


const SEARCH_ALIASES: Record<string, string> = {
  '/students': 'alumnos',
  '/aula/estudiantes': 'alumnos',
  '/teachers': 'docentes',
  '/contacts': 'personas empresas clientes facturar',
  '/courses': 'clases',
  '/aula/cursos': 'clases',
  '/calendar': 'horarios agenda clases',
  '/facturacion': 'pagos cobros cuotas mensualidades deudas facturas',
  '/financial': 'pagos caja gastos ingresos',
  '/emails': 'email',
}

function normalizeSearch(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

/** Keep search results and navigation subject to the same role permissions. */
export function searchNavigation(role: Role, query: string) {
  const terms = normalizeSearch(query).split(/\s+/).filter(Boolean)
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (!item.roles.includes(role)) return false
      const text = normalizeSearch(`${item.title} ${group.label} ${SEARCH_ALIASES[item.href] ?? ''}`)
      return terms.every((term) => text.includes(term))
    }),
  })).filter((group) => group.items.length > 0)
}
